import React, { useMemo, useState } from "react";
import styles from "./MarkdownLikeSummary.module.css";

type ListMode = "ul" | "ol";
type SummarySection = {
  id: string;
  title: string;
  content: string;
};

const KNOWN_SECTION_TITLES = new Set([
  "RESUM",
  "CONTEXT",
  "GRAFICS",
  "KPIS DESTACATS",
  "PRINCIPALS TROBALLES",
  "RECOMANACIONS",
  "IMPLICACIO",
  "IMPLICACIONS",
  "CONCLUSIONS",
]);

function normalizeHeading(text: string) {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^\*\*|\*\*$/g, "")
    .replace(/[:\-\u2014]+$/g, "")
    .trim()
    .toUpperCase();
}

function cleanHeadingLabel(text: string) {
  return text
    .replace(/^\*\*|\*\*$/g, "")
    .replace(/[:\-\u2014]+$/g, "")
    .trim();
}

function getSectionHeading(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  const normalized = normalizeHeading(trimmed);
  if (KNOWN_SECTION_TITLES.has(normalized)) {
    return cleanHeadingLabel(trimmed);
  }
  return null;
}

function parseSections(markdownText: string): SummarySection[] {
  const lines = markdownText.split("\n");
  const sections: SummarySection[] = [];

  let currentTitle = "RESUM";
  let currentContent: string[] = [];

  const flush = () => {
    const content = currentContent.join("\n").trim();
    if (!content && sections.length > 0) return;
    sections.push({
      id: `${sections.length}-${normalizeHeading(currentTitle) || "RESUM"}`,
      title: currentTitle,
      content,
    });
  };

  for (const line of lines) {
    const maybeHeading = getSectionHeading(line);
    if (maybeHeading) {
      flush();
      currentTitle = maybeHeading;
      currentContent = [];
      continue;
    }
    currentContent.push(line);
  }

  flush();
  return sections;
}

function sectionEmoji(normalized: string) {
  switch (normalized) {
    case "CONTEXT":
      return "🧭";
    case "GRAFICS":
      return "📊";
    case "KPIS DESTACATS":
      return "📌";
    case "PRINCIPALS TROBALLES":
      return "🔎";
    case "RECOMANACIONS":
      return "💡";
    case "CONCLUSIONS":
      return "✅";
    default:
      return "✨";
  }
}

function isHrLine(line: string) {
  return line.trim() === "---";
}

function isFullBoldLine(line: string) {
  // Matches: **TEXT**
  const trimmed = line.trim();
  return /^\*\*.+\*\*$/.test(trimmed);
}

function parseFullBoldInner(line: string) {
  return line.trim().replace(/^\*\*/, "").replace(/\*\*$/, "");
}

function matchBullet(line: string) {
  // - item | * item | + item | • item
  return /^(\s*)[-*+•]\s+(.+)$/.exec(line);
}

function matchNumbered(line: string) {
  // 1. item | 2) item
  return /^(\s*)(\d+)[.)]\s+(.+)$/.exec(line);
}

function isTableRow(line: string) {
  // Rough check for markdown table rows: | a | b |
  const trimmed = line.trim();
  return trimmed.startsWith("|") && trimmed.endsWith("|") && trimmed.includes("|");
}

function isTableSeparator(line: string) {
  // Rough check for markdown table separator rows: |---|:---:| etc.
  const trimmed = line.trim();
  return /^\|[\s\-:]+(\|[\s\-:]+)+\|\s*$/.test(trimmed);
}

function parseTableCells(line: string) {
  // Split by | and trim each cell. Works for both header and body rows.
  const parts = line
    .split("|")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return parts;
}

function Inline({ text }: { text: string }) {
  // Inline markdown-like:
  // - ***both*** (bold+italic)
  // - **bold**
  // - *italic*
  // - `code`
  const nodes: React.ReactNode[] = [];
  const re = /\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`/g;

  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));

    if (m[1] !== undefined) {
      // ***both***
      nodes.push(
        <strong key={`${start}-bi`}>
          <em><Inline text={m[1] as string} /></em>
        </strong>,
      );
    } else if (m[2] !== undefined) {
      // **bold**
      nodes.push(
        <strong key={`${start}-b`}><Inline text={m[2] as string} /></strong>,
      );
    } else if (m[3] !== undefined) {
      // *italic*
      nodes.push(<em key={`${start}-i`}><Inline text={m[3] as string} /></em>);
    } else if (m[4] !== undefined) {
      // `code`
      nodes.push(
        <code key={`${start}-c`} className={styles.inlineCode}>
          {m[4] as string}
        </code>,
      );
    }

    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return <>{nodes}</>;
}

function parseBlocks(markdownText: string): React.ReactNode[] {
  const lines = markdownText.split("\n");

  const blocks: React.ReactNode[] = [];
  let paragraphLines: string[] = [];

  let listMode: ListMode | null = null;
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const joined = paragraphLines.join("\n").trim();
    if (joined.length > 0) {
      blocks.push(
        <p key={`p-${blocks.length}`} className={styles.paragraph}>
          <Inline text={joined} />
        </p>,
      );
    }
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listMode || listItems.length === 0) return;
    const ListTag = listMode === "ul" ? "ul" : "ol";
    blocks.push(
      <ListTag key={`l-${blocks.length}`} className={styles.list}>
        {listItems.map((it, idx) => (
          <li key={`${blocks.length}-${idx}`}>
            <Inline text={it} />
          </li>
        ))}
      </ListTag>,
    );
    listMode = null;
    listItems = [];
  };

  const flushBoth = () => {
    flushParagraph();
    flushList();
  };

  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (!trimmed) {
      flushBoth();
      i += 1;
      continue;
    }

    if (isHrLine(trimmed)) {
      flushBoth();
      // Els separadors --- només estructuren el text; no cal mostrar-los visualment.
      i += 1;
      continue;
    }

    if (isTableRow(trimmed) && isTableSeparator(lines[i + 1] ?? "")) {
      // Handle typical layout: header row, separator row, body rows.
      flushBoth();

      const headerLine = trimmed;
      const sepLine = lines[i + 1] ?? "";
      void sepLine; // used in the separator check above

      const headerCells = parseTableCells(headerLine);
      i += 2;

      const bodyRows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i]) && !isTableSeparator(lines[i])) {
        bodyRows.push(parseTableCells(lines[i]));
        i += 1;
      }

      blocks.push(
        <div key={`t-${blocks.length}`} className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {headerCells.map((h, idx) => (
                  <th key={`th-${idx}`}>
                    <Inline text={h} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rowIdx) => (
                <tr key={`tr-${rowIdx}`}>
                  {row.map((cell, cellIdx) => (
                    <td key={`td-${rowIdx}-${cellIdx}`}>
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (isFullBoldLine(trimmed)) {
      flushBoth();
      const inner = parseFullBoldInner(trimmed);
      blocks.push(
        <div key={`h-${blocks.length}`} className={styles.sectionHeading}>
          <Inline text={inner} />
        </div>,
      );
      i += 1;
      continue;
    }

    const bullet = matchBullet(raw);
    if (bullet) {
      // New list starts; normal text closes it.
      flushParagraph();
      const content = bullet[2].trim();
      if (listMode !== "ul") {
        flushList();
        listMode = "ul";
      }
      listItems.push(content);
      i += 1;
      continue;
    }

    const numbered = matchNumbered(raw);
    if (numbered) {
      flushParagraph();
      const content = numbered[3].trim();
      if (listMode !== "ol") {
        flushList();
        listMode = "ol";
      }
      // Preserve original number-ish content is not required for display; keep just text.
      listItems.push(content);
      i += 1;
      continue;
    }

    // Regular paragraph line
    flushList();
    paragraphLines.push(trimmed);
    i += 1;
  }

  flushBoth();
  return blocks;
}

export default function MarkdownLikeSummary({ text }: { text: string }) {
  const sections = useMemo(
    () => parseSections(text).filter((section) => normalizeHeading(section.title) !== "RESUM"),
    [text],
  );
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    sections.forEach((section) => {
      initial[section.id] = normalizeHeading(section.title) === "CONTEXT";
    });
    return initial;
  });

  const toggle = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className={styles.root}>
      {sections.map((section) => {
        const normalized = normalizeHeading(section.title);
        const isContext = normalized === "CONTEXT";
        const isOpen = openSections[section.id] ?? isContext;

        if (isContext) {
          return (
            <section key={section.id} className={`${styles.sectionCard} ${styles.sectionContext}`}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionEmoji} aria-hidden="true">{sectionEmoji(normalized)}</span>
                {section.title}
              </h3>
              <div className={styles.sectionBody}>{parseBlocks(section.content)}</div>
            </section>
          );
        }

        return (
          <section key={section.id} className={styles.sectionCard}>
            <div
              role="button"
              tabIndex={0}
              className={styles.sectionToggle}
              onClick={() => toggle(section.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggle(section.id);
                }
              }}
              aria-expanded={isOpen}
            >
              <span className={styles.sectionTitle}>
                <span className={styles.sectionEmoji} aria-hidden="true">{sectionEmoji(normalized)}</span>
                {section.title}
              </span>
              <span className={styles.sectionAction}>
                <span className={styles.sectionMeta}>{isOpen ? "Amaga" : "Mostra"}</span>
                <span className={`${styles.sectionChevron} ${isOpen ? styles.sectionChevronOpen : ""}`} />
              </span>
            </div>
            {isOpen ? (
              <div className={styles.sectionBody}>{parseBlocks(section.content)}</div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

